import React, { useState, useEffect } from "react";
import { Card, Row, Col, message, Modal } from "antd";
import TableComponent from "../../../components/table/table";
import { get, post, put, del } from "../../../Api/http/httpClient";
import AgentsModal from "../../../components/modals/AgentsModal";

const { confirm } = Modal;

interface AgentsDataProps {
  user_data: any;
}

const AgentsData: React.FC<AgentsDataProps> = ({ user_data }) => {
  const [data, setData] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [sortOrder, setSortOrder] = useState("asc");
  const [sortColumn, setSortColumn] = useState("agentName");
  const [loading, setLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<any | null>(null);
  const [action, setAction] = useState<any | null>(null);
  const [formData, setFormData] = useState({
    agentName: "",
    usage: "",
    instructions: "",
    agentCategory: "",
    isGeneral: false,
    requiresParameters: false,
    establishment_id_knowledge_base: "",
    actionGroups: "[]",
    associateSupervisor: false,
    supervisorId: ""
  });

  // Se ajustan los encabezados de la tabla
  const headers_table = {
    agent_id: "ID",
    agentName: "Nombre",
    usage: "Uso",
    instructions_display: "Instrucciones",
    agentCategory: "Categoría",
    isGeneral_display: "General",
    requiresParameters_display: "Requiere Parámetros",
    actionGroups_display: "Grupos"
  };

  // Función para truncar un texto a un límite dado de caracteres
  const truncateText = (text: string, limit: number) => {
    if (!text) return "";
    return text.length > limit ? text.substring(0, limit) + "..." : text;
  };

  const getAgentsData = async () => {
    setLoading(true);
    setDeleting(null);
    try {
      const res = await get(
        `agents?establishmentID=${user_data["custom:establishment_id"]}&name_establishment=${user_data.family_name}&page=${page}&page_size=${itemsPerPage}&search=${search}&sort_by=${sortColumn}&order_by=${sortOrder}`
      );

      if (res.answer && res.data) {
        const formattedAgentItems = res.data.map((agent: any) => {
          const truncatedInstructions = truncateText(agent.instructions, 50);
          return {
            agent_id: agent.agent_id || null,
            agentName: agent.agent_name || "",
            usage: agent.usage,
            instructions: agent.instructions,
            instructions_display: truncatedInstructions,
            agentCategory: agent.agent_category || "",
            isGeneral_display: agent.isGeneral ? "Sí" : "No",
            requiresParameters: agent.requires_parameters,
            requiresParameters_display: agent.requiresParameters ? "Sí" : "No",
            actionGroups: agent.action_groups || [],
            actionGroups_display: agent.action_groups ? agent.action_groups.length : 0,
            establishment_id_knowledge_base: agent.establishment_id_knowledge_base || "",
            associateSupervisor: agent.associateSupervisor,
            supervisorId: agent.supervisor_id || ""
          };
        });
        setData(formattedAgentItems);

        const totalItems = res.total_items;
        setTotalPages(Math.ceil(totalItems / itemsPerPage));
      } else {
        message.error("No se encontraron agentes.");
      }
    } catch (error) {
      message.error("Error al obtener los datos de agentes.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAgentsData();
  }, [page, itemsPerPage, search, sortOrder, sortColumn]);

  const setActionHandler = (action: { type: string; data?: any }) => {
    if (action.type === "edit") {
      setSelectedAgent(action.data);
      setFormData({
        agentName: action.data.agentName,
        usage: action.data.usage,
        instructions: action.data.instructions || "",
        agentCategory: action.data.agentCategory || "",
        isGeneral: action.data.isGeneral,
        requiresParameters: action.data.requiresParameters,
        establishment_id_knowledge_base: action.data.establishment_id_knowledge_base || "",
        actionGroups: JSON.stringify(action.data.actionGroups || []),
        associateSupervisor: action.data.associateSupervisor || false,
        supervisorId: action.data.supervisorId || ""
      });
      setAction(action);
      handleOpenModal();
    } else if (action.type === "delete") {
      setAction(action);
      showDeleteConfirm(action.data);
    } else if (action.type === "create") {
      setFormData({
        agentName: "",
        usage: "",
        instructions: "",
        agentCategory: "",
        isGeneral: false,
        requiresParameters: false,
        establishment_id_knowledge_base: "",
        actionGroups: "[]",
        associateSupervisor: false,
        supervisorId: ""
      });
      setAction(action);
      handleOpenModal();
    } else if (action.type === "reload") {
      getAgentsData();
    }
  };

  const showDeleteConfirm = (agent: any) => {
    confirm({
      title: "¿Estás seguro de eliminar este agente?",
      content: `Agente: ${agent.agentName}`,
      okText: "Sí, eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk() {
        handleDelete(agent);
      },
      onCancel() {
        setDeleting(null);
      },
    });
  };

  const handleDelete = async (agent: any) => {
    setDeleting(agent.agent_id);
    try {
      const res = await del("agents", {
        establishmentID: user_data["custom:establishment_id"],
        name_establishment: user_data.family_name,
        agent_id: agent.agent_id,
        agentName: agent.agentName
      });
      if (res && res.answer) {
        message.success("Agente eliminado correctamente.");
        getAgentsData();
      } else {
        message.error("Error al eliminar el agente.");
      }
    } catch (error) {
      message.error("Error al eliminar el agente.");
    } finally {
      setDeleting(null);
    }
  };

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedAgent(null);
    setAction(null);
  };

  const handleSave = async () => {
    setModalLoading(true);
    try {
      const formattedFormData = {
        agentName: formData.agentName,
        usage: formData.usage,
        instructions: formData.instructions,
        isGeneral: formData.isGeneral,
        agentCategory: formData.agentCategory,
        requires_parameters: formData.requiresParameters,
        establishment_id_knowledge_base: formData.establishment_id_knowledge_base,
        action_groups: JSON.parse(formData.actionGroups || "[]"),
        associateSupervisor: formData.associateSupervisor,
        supervisorId: formData.supervisorId
      };
      const res = await post("agents", {
        establishmentID: user_data["custom:establishment_id"],
        name_establishment: user_data.family_name,
        ...formattedFormData,
      });
      if (res && res.answer) {
        message.success("Agente creado correctamente.");
        handleCloseModal();
        getAgentsData();
      } else {
        message.error("Error al crear el agente.");
      }
    } catch (error) {
      message.error("Error al crear el agente.");
    } finally {
      setModalLoading(false);
    }
  };

  const handleUpdate = async () => {
    setModalLoading(true);
    try {
      if (selectedAgent) {
        const formattedFormData = {
          agentName: formData.agentName,
          usage: formData.usage,
          instructions: formData.instructions,
          isGeneral: formData.isGeneral,
          agentCategory: formData.agentCategory,
          requires_parameters: formData.requiresParameters,
          establishment_id_knowledge_base: formData.establishment_id_knowledge_base,
          action_groups: JSON.parse(formData.actionGroups || "[]"),
          associateSupervisor: formData.associateSupervisor,
          supervisorId: formData.supervisorId
        };
        const res = await put("agents", {
          establishmentID: user_data["custom:establishment_id"],
          name_establishment: user_data.family_name,
          agent_id: selectedAgent.agent_id,
          ...formattedFormData,
        });
        if (res && res.answer) {
          const updatedData = data.map((item) =>
            item.agent_id === selectedAgent.agent_id ? { ...item, ...formattedFormData } : item
          );
          setData(updatedData);
          message.success("Agente actualizado correctamente.");
          handleCloseModal();
          getAgentsData();
        } else {
          message.error("Error al actualizar el agente.");
        }
      } else {
        message.error("No se seleccionó ningún agente para actualizar.");
      }
    } catch (error) {
      message.error("Error al actualizar el agente.");
    } finally {
      setModalLoading(false);
    }
  };

  // Se modifica handleChange para tratar campos booleanos por separado
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    if (id === "associateSupervisor" || id === "requiresParameters" || id === "isGeneral") {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [id]: value === "true"
      }));
    } else {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [id]: value,
      }));
    }
  };

  return (
    <Row justify="center" style={{ marginTop: "20px" }}>
      <Col span={23} style={{ paddingLeft: "20px", paddingRight: "20px" }}>
        <Card bordered={false} style={{ borderRadius: "10px", boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)", padding: "0px" }}>
          <TableComponent
            data={data}
            headers_table={headers_table}
            loading={loading}
            setSearch={setSearch}
            setAction={setActionHandler}
            totalPages={totalPages}
            setPage={setPage}
            page={page}
            setItemsPerPage={setItemsPerPage}
            itemsPerPage={itemsPerPage}
            setSortOrder={setSortOrder}
            setSortColumn={setSortColumn}
            deleting={deleting}
          />
        </Card>
      </Col>

      <AgentsModal
        open={modalOpen}
        onClose={handleCloseModal}
        formData={formData}
        handleChange={handleChange}
        handleSave={handleSave}
        handleUpdate={handleUpdate}
        action={action}
        loading={modalLoading}
      />
    </Row>
  );
};

export default AgentsData;